/**
 * Incorporation Post (Post-Incorporation Package, M32101) — 폼 → 변수 mapper.
 *
 * 12개 문서 (Phase 1 필수 7 + Phase 2 정관 3 + Phase 3 조건부 2) 전체 변수를 다룸.
 * 변수 이름은 Incorporation_Post_Variable_Map.md 기준.
 *
 * 반복 블록:
 *   - [[if directors]] / [[/if directors]] — 다수 이사 케이스
 *   - [[if is_sole_director]] / [[/if is_sole_director]] — 단독 이사 케이스
 *   - [[if cosec]] / [[/if cosec]] — 비서 다명
 *   - [[if Allottee]] / [[/if Allottee]] — 주주별 배정 주식
 */

export interface AddressOption {
  type: string;
  label: string;
  value: string;
}

export interface DirectorInput {
  /** L_OFFICER.CONTACT_CD — Signer 매핑용 */
  contactCd?: string;
  name: string;
  email?: string;
  /** Letter of Indemnity 등에서 사용하는 주소 */
  address?: string;
  /** chooser UI 용 후보 주소 목록 */
  addresses?: AddressOption[];
  /** 현재 선택된 주소의 addresses[] 내 index — chip highlight 용 */
  addressSelectedIdx?: number;
  /** Nominee 여부 — Letter of Indemnity 노출 조건 */
  isNominee?: boolean;
  /** L_OFFICER.APPOINTMENT_DATE 등재일 (이사 개별). ISO YYYY-MM-DD. */
  dateOfAppointment?: string;
}

export interface CosecInput {
  /** L_OFFICER.CONTACT_CD — Signer 매핑용 */
  contactCd?: string;
  name: string;
  /** MAICSA / MIA / LS 등 라이선스 번호 */
  licenseNo: string;
  /** Section 236(3) 용 */
  nricNo?: string;
  firm?: string;
  tel?: string;
  email?: string;
  fax?: string;
  ssmCertNo?: string;
  /** Petty Cash / Members WR 의 수신 주소 */
  companyName?: string;
  address?: string;
  /** chooser UI 용 후보 주소 목록 */
  addresses?: AddressOption[];
  /** 현재 선택된 주소의 addresses[] 내 index — chip highlight 용 */
  addressSelectedIdx?: number;
  /** L_OFFICER.APPOINTMENT_DATE 등재일 (비서 개별). ISO YYYY-MM-DD. */
  dateOfAppointment?: string;
}

export interface AllotteeInput {
  contactCd?: string;
  name: string;
  shares: number;
  /** 기존 entity 보유 shares (display only) — 사용자 참고용 hint, mapper / backend 에 안 보냄. */
  _currentlyHolds?: number;
}

export interface LodgerInput {
  name: string;
  nricNo: string;
  address: string;
  phoneNo: string;
  email: string;
}

export interface IncorporationPostInput {
  // 회사 정보
  companyName: string;
  companyRegistrationNo: string;
  registeredOfficeAddress: string;
  businessAddress?: string;
  companyTel?: string;
  incorporationDate?: string;

  // 이사 (다수 vs 단독 자동 분기)
  directors: DirectorInput[];
  /** sole-director 강제 — 1명일 때 자동, override 가능 */
  isSoleDirector?: boolean;

  // 비서 (다명 가능)
  cosecs: CosecInput[];

  // 회계
  financialYearEnd?: string;
  firstAccountFrom?: string;
  firstAccountTo?: string;

  // 주식 / Allottee
  totalShares?: number | string;
  sharePrice?: number | string;
  totalCashAmount?: number | string;
  totalCashAmountWords?: string;
  allottees: AllotteeInput[];

  // 감사인 / 세무대리인
  auditorName?: string;
  auditorAddress?: string;
  auditorContactCd?: string;
  auditorDateOfAppointment?: string;
  taxAgentName?: string;
  taxAgentAddress?: string;
  taxAgentRegistrationBracket?: string;
  taxAgentEmail?: string;
  taxAgentApprovalNo?: string;
  taxAgentOfficerName?: string;

  // LHDN 전용
  lhdnRefNo?: string;
  /** LHDN 서신에 들어가는 (서명 없는) 이사 1인 이름 */
  directorNameForLhdn?: string;

  // 정관 (Constitution)
  hasConstitution?: boolean;
  businessObject1?: string;
  businessObject2?: string;
  businessObject3?: string;
  constitutionClauseNo?: string;

  // Common Seal
  hasCommonSeal?: boolean;

  // Lodger (Constitution Annexure A 제출인)
  lodger?: LodgerInput;

  // Nominee Director Indemnity (조건부)
  nomineeDirectorName?: string;
  nomineeDirectorAddress?: string;

  // 날짜
  resolutionDate?: string;
  letterDate?: string;
  declarationDate?: string;
  circulationDate?: string;
  deadlineDate?: string;
}

/** 한 이사 row 를 [[if directors]] 반복 블록의 inner 객체로 매핑 */
function buildDirectorLoopRow(d: DirectorInput, idx: number) {
  return {
    director_no: String(idx + 1),
    director_name: String(d.name || '').trim(),
    director_address: String(d.address || '').trim(),
  };
}

/** 한 비서 row → [[if cosec]] 반복 블록 inner */
function buildCosecLoopRow(c: CosecInput, idx: number) {
  return {
    cosec_no: String(idx + 1),
    cosec_name: String(c.name || '').trim(),
    cosec_license_no: String(c.licenseNo || '').trim(),
  };
}

/** Allottee row → [[if Allottee]] 반복 블록 inner */
function buildAllotteeLoopRow(a: AllotteeInput) {
  return {
    allottee_name: String(a.name || '').trim(),
    allotted_shares: String(a.shares || 0),
  };
}

/** Malaysia NRIC 표준 포맷: 12자리 숫자면 XXXXXX-XX-XXXX 로 표시. */
function formatMalaysiaNricForDoc(raw: any): string {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  const digits = s.replace(/\D/g, '');
  if (digits.length === 12) {
    return `${digits.slice(0, 6)}-${digits.slice(6, 8)}-${digits.slice(8)}`;
  }
  return s;
}

export function buildIncorporationPostVariables(input: IncorporationPostInput): Record<string, any> {
  const dirs = (input.directors || []).filter((d) => d?.name?.trim());
  const cosecs = (input.cosecs || []).filter((c) => c?.name?.trim());
  const allottees = (input.allottees || []).filter((a) => a?.name?.trim());

  const isSole = input.isSoleDirector ?? dirs.length === 1;

  // 비서 1명 정보 — Section 236(3) / Petty Cash / Members WR 용. Primary = 첫 비서.
  const primaryCosec = cosecs[0] || ({} as CosecInput);

  const vars: Record<string, any> = {
    // ── 회사 정보 ──
    company_name: String(input.companyName || '').trim(),
    company_registration_no: String(input.companyRegistrationNo || '').trim(),
    registered_office_address: String(input.registeredOfficeAddress || '').trim(),
    business_address: String(input.businessAddress || '').trim(),
    company_tel: String(input.companyTel || '').trim(),
    incorporation_date: String(input.incorporationDate || '').trim(),

    // ── 레지스터 등록용 원본 배열 (mapper 가 directors/allottees 를 템플릿 토글로 변환하므로 원본을 별도 보존) ──
    //   백엔드 register-incorporation-officers 가 이 키들을 우선 읽어 Before/After 비교.
    __directorsRaw: dirs.map((d) => ({
      contactCd: String((d as any).contactCd || '').trim(),
      name: String(d.name || '').trim(),
      email: String((d as any).email || '').trim(),
      address: String((d as any).address || '').trim(),
      nationality: String((d as any).nationality || (d as any).nationalityText || '').trim(),
      dateOfBirth: String((d as any).dateOfBirth || '').trim(),
      identificationNo: String((d as any).identificationNo || (d as any).idNo || (d as any).contactRegNo || '').trim(),
      isNominee: !!(d as any).isNominee,
      dateOfAppointment: String((d as any).dateOfAppointment || '').trim(),
    })),
    __cosecsRaw: cosecs.map((c) => ({
      contactCd: String((c as any).contactCd || '').trim(),
      name: String(c.name || '').trim(),
      email: String((c as any).email || '').trim(),
      address: String((c as any).address || '').trim(),
      licenseNo: String((c as any).licenseNo || '').trim(),
      nricNo: String((c as any).nricNo || '').trim(),
      dateOfAppointment: String((c as any).dateOfAppointment || '').trim(),
    })),
    // allottees 는 share class 별 row 가 있어 동일 contactCd 가 중복될 수 있음.
    //   레지스터용으로는 contactCd 기준 dedupe — 같은 사람은 1행만, shares 는 합산.
    //   contactCd 없는 항목(수기)은 name+합산 키로 dedupe.
    __allotteesRaw: (() => {
      const map = new Map<string, { contactCd: string; name: string; shares: number }>();
      for (const a of allottees) {
        const cc = String((a as any).contactCd || '').trim();
        const nm = String(a.name || '').trim();
        if (!nm) continue;
        const key = cc || `name:${nm.toUpperCase()}`;
        const sh = Number((a as any).shares || 0);
        const existing = map.get(key);
        if (existing) {
          existing.shares += sh;
        } else {
          map.set(key, { contactCd: cc, name: nm, shares: sh });
        }
      }
      return Array.from(map.values());
    })(),
    // 회사 auditor 는 단일 — name+address (+ Person Lookup 시 contactCd).
    //   비어있으면 register 시 무시.
    __auditorRaw: input.auditorName
      ? {
          contactCd: String(input.auditorContactCd || '').trim(),
          name: String(input.auditorName || '').trim(),
          address: String(input.auditorAddress || '').trim(),
          dateOfAppointment: String(input.auditorDateOfAppointment || '').trim(),
        }
      : null,

    // ── 이사 분기 (다수 vs 단독) ──
    directors_names: dirs.map((d) => d.name).join(', '),
    sole_director_name: isSole && dirs[0] ? String(dirs[0].name || '').trim() : '',
    // [[if directors]] 블록은 "다수 이사가 있을 때 한 번만" 출력되는 conditional toggle.
    //   engine 은 array length 만큼 loop 하므로 single-item array 로 1회 출력 보장.
    //   row 에 directors_names 도 포함 (loop-row lookup 우선 시 안전망).
    directors: !isSole && dirs.length > 0
      ? [{ directors_names: dirs.map((d) => d.name).join(', ') }]
      : [],
    is_sole_director: isSole ? [{ sole_director_name: dirs[0]?.name || '' }] : [],
    // 템플릿 [[if isSoleDirector]] / [[if !isSoleDirector]] / [[if isSoledirect]] / [[if isDirectors]] 조건부 분기용.
    //   conditional (endif) 패턴은 *scalar* validVariableData 를 평가 — array 면 repeating loop 로 인식되어
    //   conditional 평가단에서 누락됨. 'true'/'' 문자열로 truthy/falsy 직접 셋팅.
    //   case-insensitive 매치라 isSoleDirector / isSoledirector / issoledirector 모두 동작.
    //   (기존 is_sole_director / directors 의 array 토글은 [[if NAME]]...[[/if NAME]] 루프용 — 그대로 유지)
    isSoleDirector: isSole ? 'true' : '',
    isSoledirect: isSole ? 'true' : '',
    isDirectors: !isSole && dirs.length > 0 ? 'true' : '',
    // 다수 이사 row 별 정보 — 필요 시 별도 loop key 로 노출.
    direct_his: !isSole ? dirs.map(buildDirectorLoopRow) : [],

    // ── 비서 (다명) ──
    cosec: cosecs.map(buildCosecLoopRow),
    cosec_name: String(primaryCosec.name || '').trim(),
    cosec_nric_no: formatMalaysiaNricForDoc(primaryCosec.nricNo),
    cosec_firm: String(primaryCosec.firm || '').trim(),
    cosec_tel: String(primaryCosec.tel || '').trim(),
    cosec_email: String(primaryCosec.email || '').trim(),
    cosec_fax: String(primaryCosec.fax || '').trim(),
    cosec_license_no: String(primaryCosec.licenseNo || '').trim(),
    cosec_ssm_cert_no: String(primaryCosec.ssmCertNo || '').trim(),
    cosec_company_name: String(primaryCosec.companyName || primaryCosec.firm || '').trim(),
    cosec_address: String(primaryCosec.address || '').trim(),

    // ── 회계 ──
    financial_year_end: String(input.financialYearEnd || '').trim(),
    first_account_from: String(input.firstAccountFrom || '').trim(),
    first_account_to: String(input.firstAccountTo || '').trim(),

    // ── 주식 / Allottee ──
    total_shares: input.totalShares != null ? String(input.totalShares) : '',
    share_price: input.sharePrice != null ? String(input.sharePrice) : '',
    total_cash_amount: input.totalCashAmount != null ? String(input.totalCashAmount) : '',
    total_cash_amount_words: String(input.totalCashAmountWords || '').trim(),
    // ⚠️ 키는 lowercase — 백엔드 expandRepeatingSections 가 sectionName 을 `.toLowerCase()` 후
    //   `repeatingData[name]` / `repeatingData[name + 's']` 로 lookup. 'Allottee' → 'allottee' 매칭.
    allottee: allottees.map(buildAllotteeLoopRow),

    // ── 감사인 / 세무대리인 ──
    auditor_name: String(input.auditorName || '').trim(),
    auditor_address: String(input.auditorAddress || '').trim(),
    tax_agent_name: String(input.taxAgentName || '').trim(),
    tax_agent_address: String(input.taxAgentAddress || '').trim(),
    tax_agent_registration_bracket: String(input.taxAgentRegistrationBracket || '').trim(),
    tax_agent_email: String(input.taxAgentEmail || '').trim(),
    tax_agent_approval_no: String(input.taxAgentApprovalNo || '').trim(),
    tax_agent_officer_name: String(input.taxAgentOfficerName || '').trim(),

    // ── LHDN ──
    lhdn_ref_no: String(input.lhdnRefNo || '').trim(),
    director_name: String(input.directorNameForLhdn || dirs[0]?.name || '').trim(),

    // ── 정관 ──
    business_object_1: String(input.businessObject1 || '').trim(),
    business_object_2: String(input.businessObject2 || '').trim(),
    business_object_3: String(input.businessObject3 || '').trim(),
    constitution_clause_no: String(input.constitutionClauseNo || '').trim(),

    // ── Lodger ──
    lodger_name: String(input.lodger?.name || '').trim(),
    lodger_nric_no: formatMalaysiaNricForDoc(input.lodger?.nricNo),
    lodger_address: String(input.lodger?.address || '').trim(),
    lodger_phone_no: String(input.lodger?.phoneNo || '').trim(),
    lodger_email: String(input.lodger?.email || '').trim(),

    // ── Nominee Director Indemnity ──
    nominee_director_name: String(input.nomineeDirectorName || '').trim(),
    nominee_director_address: String(input.nomineeDirectorAddress || '').trim(),

    // ── 날짜 ──
    resolution_date: String(input.resolutionDate || '').trim(),
    letter_date: String(input.letterDate || '').trim(),
    declaration_date: String(input.declarationDate || '').trim(),
    circulation_date: String(input.circulationDate || '').trim(),
    deadline_date: String(input.deadlineDate || '').trim(),
  };

  return vars;
}

/**
 * 어떤 문서가 selection 에 포함됐는지 감지 — panel UI 섹션 토글에 사용.
 *   templateName 정규식 매치.
 */
export interface IncorporationPostDocFlags {
  hasFirstBoardMeeting: boolean;
  hasDcrAppointCosec: boolean;
  hasSection236_3: boolean;
  hasPettyCash: boolean;
  hasAuditorLetter: boolean;
  hasTaxAgentLetter: boolean;
  hasLhdnLetter: boolean;
  hasDcrConstitution: boolean;
  hasMembersWrConstitution: boolean;
  hasConstitutionAnnexure: boolean;
  hasDcrCommonSeal: boolean;
  hasLetterOfIndemnity: boolean;
}

export function detectIncorporationPostDocFlags(
  documents: Array<{ templateName?: string; docCode?: string }>
): IncorporationPostDocFlags {
  const names = (documents || [])
    .map((d) => String(d?.templateName || d?.docCode || '').toLowerCase())
    .filter(Boolean);
  const any = (re: RegExp) => names.some((n) => re.test(n));
  return {
    hasFirstBoardMeeting: any(/first.*board|board.*director.*meeting/i),
    hasDcrAppointCosec: any(/appoint.*co.?sec|first.*co.?sec|appointment.*secretary/i),
    hasSection236_3: any(/236.?\(?3\)?|section\s*236/i),
    hasPettyCash: any(/petty\s*cash/i),
    hasAuditorLetter: any(/auditor.*letter|letter.*auditor|appointment.*auditor/i),
    hasTaxAgentLetter: any(/tax\s*agent.*letter|letter.*tax\s*agent/i),
    hasLhdnLetter: any(/lhdn|inland\s*revenue/i),
    hasDcrConstitution: any(/dcr.*constitution|adoption.*constitution|adopt.*constitution/i),
    hasMembersWrConstitution: any(/members.*written.*resolution.*constitution|members.*wr.*const/i),
    hasConstitutionAnnexure: any(/annexure.*a|constitution.*annexure|^constitution$|constitution\.docx/i),
    hasDcrCommonSeal: any(/common\s*seal/i),
    hasLetterOfIndemnity: any(/indemnity|letter\s*of\s*indemnity/i),
  };
}
